/**
 * Letter Templates Management Page
 * Phase T1: Complete Document Template System
 * 
 * Features:
 * - Template List with filters
 * - Template Editor with placeholder helper
 * - Manual fields schema editor
 * - Publish/Archive actions
 * - Preview with mock data
 * 
 * Access: HR_MANAGER + SUPER_ADMIN (edit), HR roles (view only)
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { 
  FileText, 
  Search, 
  Plus, 
  Edit2, 
  Eye, 
  Archive, 
  CheckCircle2, 
  AlertTriangle,
  Trash2,
  Save,
  Upload,
  Code,
  Info,
  Copy,
  X
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { sanitizeHtml } from '@/lib/sanitize';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Status badge colors
const STATUS_COLORS = {
  draft: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Draft' },
  published: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'Published' },
  archived: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Archived' },
};

// Manual field types
const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'number', label: 'Number' },
  { value: 'date', label: 'Date' },
  { value: 'currency', label: 'Currency (SAR)' },
  { value: 'select', label: 'Dropdown' },
];

export default function LetterTemplatesPage() {
  const { token, user } = useAuth();
  const { isRTL } = useLanguage();
  
  // State
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [letterTypes, setLetterTypes] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [letterTypeFilter, setLetterTypeFilter] = useState('all');
  
  // Editor state
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewLanguage, setPreviewLanguage] = useState('ar');
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    content_ar: '',
    content_en: '',
    manual_fields: [],
    change_notes: ''
  });
  
  // Dialogs
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [showArchiveDialog, setShowArchiveDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [showPlaceholdersDialog, setShowPlaceholdersDialog] = useState(false);
  
  const [saving, setSaving] = useState(false);
  
  // Check if user can edit templates
  const canEdit = user?.role === 'super_admin' || user?.role === 'hr_manager';
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        const [templatesRes, typesRes] = await Promise.all([
          axios.get(`${API_URL}/letters/templates`, { headers, params: { page_size: 200 } }),
          axios.get(`${API_URL}/letters/types`, { headers })
        ]);
        
        setTemplates(templatesRes.data.templates || []);
        setLetterTypes(typesRes.data.letter_types || []);
        
        // Fetch placeholders
        try {
          // Get any template ID for placeholders (they're global)
          if (templatesRes.data.templates?.length > 0) {
            const placeholdersRes = await axios.get(
              `${API_URL}/letters/templates/${templatesRes.data.templates[0].id}/placeholders`,
              { headers }
            );
            setPlaceholders(placeholdersRes.data.auto_placeholders || []);
          }
        } catch (e) {
          console.log('Placeholders fetch skipped');
        }
        
      } catch (error) {
        console.error('Failed to fetch templates:', error);
        toast.error('Failed to load templates');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchData();
    }
  }, [token]);
  
  // Filter templates
  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      // Status filter
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      
      // Letter type filter
      if (letterTypeFilter !== 'all' && t.letter_type_code !== letterTypeFilter) return false;
      
      // Search
      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        const letterType = letterTypes.find(lt => lt.code === t.letter_type_code);
        const typeName = letterType?.name_en?.toLowerCase() || '';
        const typeNameAr = letterType?.name_ar || '';
        
        if (
          !t.letter_type_code.toLowerCase().includes(search) &&
          !typeName.includes(search) &&
          !typeNameAr.includes(search)
        ) {
          return false;
        }
      }
      
      return true;
    });
  }, [templates, statusFilter, letterTypeFilter, searchQuery, letterTypes]);
  
  // Group templates by letter type
  const groupedTemplates = useMemo(() => {
    const groups = {};
    filteredTemplates.forEach(t => {
      if (!groups[t.letter_type_code]) {
        groups[t.letter_type_code] = [];
      }
      groups[t.letter_type_code].push(t);
    });
    // Sort by version descending within each group
    Object.keys(groups).forEach(code => {
      groups[code].sort((a, b) => b.version - a.version);
    });
    return groups;
  }, [filteredTemplates]);
  
  // Get letter type name
  const getLetterTypeName = (code) => {
    const lt = letterTypes.find(t => t.code === code);
    return isRTL ? lt?.name_ar || code : lt?.name_en || code;
  };
  
  // Handle template selection
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    setEditForm({
      content_ar: template.content_ar || '',
      content_en: template.content_en || '',
      manual_fields: template.manual_fields || [],
      change_notes: ''
    });
    setEditMode(false);
  };
  
  // Save template changes
  const handleSaveTemplate = async () => {
    if (!selectedTemplate || selectedTemplate.status !== 'draft') {
      toast.error('Only draft templates can be edited');
      return;
    }
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API_URL}/letters/templates/${selectedTemplate.id}`,
        editForm,
        { headers }
      );
      
      // Refresh templates
      const res = await axios.get(`${API_URL}/letters/templates`, { headers, params: { page_size: 200 } });
      setTemplates(res.data.templates || []);
      
      // Update selected template
      const updated = res.data.templates.find(t => t.id === selectedTemplate.id);
      if (updated) {
        setSelectedTemplate(updated);
        setEditForm({
          content_ar: updated.content_ar || '',
          content_en: updated.content_en || '',
          manual_fields: updated.manual_fields || [],
          change_notes: ''
        });
      }
      
      toast.success('Template saved successfully');
      setEditMode(false);
    } catch (error) {
      console.error('Save failed:', error);
      toast.error(error.response?.data?.detail?.error || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };
  
  // Publish template
  const handlePublishTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.post(
        `${API_URL}/letters/templates/${selectedTemplate.id}/publish`,
        {},
        { headers }
      );
      
      toast.success(`Template v${selectedTemplate.version} published successfully`);
      setShowPublishDialog(false);
      
      // Refresh templates
      const listRes = await axios.get(`${API_URL}/letters/templates`, { headers, params: { page_size: 200 } });
      setTemplates(listRes.data.templates || []);
      
      // Select the published template
      const published = listRes.data.templates.find(t => t.id === selectedTemplate.id);
      if (published) {
        setSelectedTemplate(published);
      }
    } catch (error) {
      console.error('Publish failed:', error);
      toast.error(error.response?.data?.detail?.error || 'Failed to publish template');
    } finally {
      setSaving(false);
    }
  };
  
  // Archive template
  const handleArchiveTemplate = async () => {
    if (!selectedTemplate) return;
    
    setSaving(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_URL}/letters/templates/${selectedTemplate.id}/archive`,
        {},
        { headers }
      );
      
      toast.success('Template archived');
      setShowArchiveDialog(false);
      
      // Refresh templates
      const res = await axios.get(`${API_URL}/letters/templates`, { headers, params: { page_size: 200 } });
      setTemplates(res.data.templates || []);
      
      setSelectedTemplate(null);
    } catch (error) {
      console.error('Archive failed:', error);
      toast.error(error.response?.data?.detail?.error || 'Failed to archive template');
    } finally {
      setSaving(false);
    }
  };
  
  // Preview template
  const handlePreviewTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API_URL}/letters/templates/${selectedTemplate.id}/preview`,
        { headers }
      );
      setPreviewData(res.data);
      setShowPreviewDialog(true);
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error('Failed to generate preview');
    }
  };
  
  // Add manual field
  const handleAddManualField = () => {
    setEditForm(prev => ({
      ...prev,
      manual_fields: [
        ...prev.manual_fields,
        {
          key: '',
          label_ar: '',
          label_en: '',
          field_type: 'text',
          required: false,
          placeholder_ar: '',
          placeholder_en: '',
          options: []
        }
      ]
    }));
  };
  
  // Update manual field
  const handleUpdateManualField = (index, field, value) => {
    setEditForm(prev => ({
      ...prev,
      manual_fields: prev.manual_fields.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };
  
  // Remove manual field
  const handleRemoveManualField = (index) => {
    setEditForm(prev => ({
      ...prev,
      manual_fields: prev.manual_fields.filter((_, i) => i !== index)
    }));
  };
  
  // Copy placeholder to clipboard
  const copyPlaceholder = (key) => {
    navigator.clipboard.writeText(`{{${key}}}`);
    toast.success(`Copied {{${key}}} to clipboard`);
  };
  
  if (loading) {
    return (
      <div className="p-6 space-y-6" data-testid="templates-loading">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Skeleton className="h-[600px]" />
          <Skeleton className="h-[600px] lg:col-span-2" />
        </div>
      </div>
    );
  }
  
  return (
    <div className={cn("p-6 space-y-6", isRTL && "rtl")} data-testid="letter-templates-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="templates-title">
            {isRTL ? 'قوالب الخطابات' : 'Letter Templates'}
          </h1>
          <p className="text-slate-600 text-sm">
            {isRTL 
              ? 'إدارة قوالب الخطابات والوثائق'
              : 'Manage letter and document templates'}
          </p>
        </div>
        
        {canEdit && (
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-template-btn">
            <Plus className="h-4 w-4 mr-2" />
            {isRTL ? 'قالب جديد' : 'New Template'}
          </Button>
        )}
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4">
            <div className="text-2xl font-bold text-amber-700">
              {templates.filter(t => t.status === 'draft').length}
            </div>
            <div className="text-sm text-amber-600">{isRTL ? 'مسودات' : 'Drafts'}</div>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="py-4">
            <div className="text-2xl font-bold text-emerald-700">
              {templates.filter(t => t.status === 'published').length}
            </div>
            <div className="text-sm text-emerald-600">{isRTL ? 'منشورة' : 'Published'}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-4">
            <div className="text-2xl font-bold text-slate-600">
              {templates.filter(t => t.status === 'archived').length}
            </div>
            <div className="text-sm text-slate-500">{isRTL ? 'مؤرشفة' : 'Archived'}</div>
          </CardContent>
        </Card>
      </div>
      
      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Template List */}
        <Card className="lg:col-span-1 h-fit max-h-[700px] overflow-hidden flex flex-col">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">{isRTL ? 'القوالب' : 'Templates'}</CardTitle>
            
            {/* Filters */}
            <div className="space-y-3 mt-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={isRTL ? 'بحث...' : 'Search...'}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="templates-search"
                />
              </div>
              <div className="flex gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="flex-1" data-testid="status-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{isRTL ? 'الكل' : 'All Status'}</SelectItem>
                    <SelectItem value="draft">{isRTL ? 'مسودة' : 'Draft'}</SelectItem>
                    <SelectItem value="published">{isRTL ? 'منشور' : 'Published'}</SelectItem>
                    <SelectItem value="archived">{isRTL ? 'مؤرشف' : 'Archived'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="flex-1 overflow-auto">
            <div className="space-y-2">
              {Object.entries(groupedTemplates).map(([code, versions]) => (
                <div key={code} className="space-y-1">
                  <div className="text-xs font-medium text-slate-500 px-2 py-1 bg-slate-50 rounded">
                    {getLetterTypeName(code)}
                  </div>
                  {versions.map(template => {
                    const statusStyle = STATUS_COLORS[template.status] || STATUS_COLORS.draft;
                    const isSelected = selectedTemplate?.id === template.id;
                    
                    return (
                      <button
                        key={template.id}
                        onClick={() => handleSelectTemplate(template)}
                        className={cn(
                          "w-full text-left p-3 rounded-lg border transition-all",
                          isSelected 
                            ? "border-blue-500 bg-blue-50" 
                            : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                        )}
                        data-testid={`template-item-${template.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-slate-900">
                            v{template.version}
                          </span>
                          <Badge className={cn(statusStyle.bg, statusStyle.text, "text-xs")}>
                            {statusStyle.label}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {template.status === 'published' && template.published_at
                            ? `Published: ${new Date(template.published_at).toLocaleDateString()}`
                            : `Updated: ${new Date(template.updated_at).toLocaleDateString()}`
                          }
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
              
              {Object.keys(groupedTemplates).length === 0 && (
                <div className="text-center py-8 text-slate-400">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>{isRTL ? 'لا توجد قوالب' : 'No templates found'}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* Template Editor */}
        <Card className="lg:col-span-2">
          {selectedTemplate ? (
            <>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {getLetterTypeName(selectedTemplate.letter_type_code)}
                    </CardTitle>
                    <CardDescription>
                      Version {selectedTemplate.version} • {STATUS_COLORS[selectedTemplate.status]?.label}
                    </CardDescription>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowPlaceholdersDialog(true)}
                    >
                      <Code className="h-4 w-4 mr-1" />
                      {isRTL ? 'المتغيرات' : 'Placeholders'}
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handlePreviewTemplate}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      {isRTL ? 'معاينة' : 'Preview'}
                    </Button>
                    
                    {canEdit && selectedTemplate.status === 'draft' && (
                      <>
                        {editMode ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setEditMode(false)}
                            >
                              <X className="h-4 w-4 mr-1" />
                              {isRTL ? 'إلغاء' : 'Cancel'}
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveTemplate}
                              disabled={saving}
                            >
                              <Save className="h-4 w-4 mr-1" />
                              {saving ? '...' : isRTL ? 'حفظ' : 'Save'}
                            </Button>
                          </>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditMode(true)}
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            {isRTL ? 'تعديل' : 'Edit'}
                          </Button>
                        )}
                        
                        <Button
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => setShowPublishDialog(true)}
                        >
                          <Upload className="h-4 w-4 mr-1" />
                          {isRTL ? 'نشر' : 'Publish'}
                        </Button>
                      </>
                    )}
                    
                    {canEdit && selectedTemplate.status !== 'archived' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-slate-500 hover:text-slate-700"
                        onClick={() => setShowArchiveDialog(true)}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <Tabs defaultValue="content" className="space-y-4">
                  <TabsList>
                    <TabsTrigger value="content">
                      {isRTL ? 'المحتوى' : 'Content'}
                    </TabsTrigger>
                    <TabsTrigger value="manual-fields">
                      {isRTL ? 'الحقول اليدوية' : 'Manual Fields'}
                      {editForm.manual_fields.length > 0 && (
                        <Badge variant="secondary" className="ml-2">
                          {editForm.manual_fields.length}
                        </Badge>
                      )}
                    </TabsTrigger>
                    <TabsTrigger value="info">
                      {isRTL ? 'المعلومات' : 'Info'}
                    </TabsTrigger>
                  </TabsList>
                  
                  {/* Content Tab */}
                  <TabsContent value="content" className="space-y-4">
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          {isRTL ? 'المحتوى العربي' : 'Arabic Content'}
                        </Label>
                        <Textarea
                          value={editForm.content_ar}
                          onChange={(e) => setEditForm(prev => ({ ...prev, content_ar: e.target.value }))}
                          disabled={!editMode}
                          className="min-h-[300px] font-mono text-sm"
                          dir="rtl"
                          data-testid="content-ar"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          {isRTL ? 'المحتوى الإنجليزي' : 'English Content'}
                        </Label>
                        <Textarea
                          value={editForm.content_en}
                          onChange={(e) => setEditForm(prev => ({ ...prev, content_en: e.target.value }))}
                          disabled={!editMode}
                          className="min-h-[300px] font-mono text-sm"
                          data-testid="content-en"
                        />
                      </div>
                    </div>
                    
                    {editMode && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">
                          {isRTL ? 'ملاحظات التغيير' : 'Change Notes'}
                        </Label>
                        <Input
                          value={editForm.change_notes}
                          onChange={(e) => setEditForm(prev => ({ ...prev, change_notes: e.target.value }))}
                          placeholder={isRTL ? 'وصف التغييرات...' : 'Describe your changes...'}
                        />
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Manual Fields Tab */}
                  <TabsContent value="manual-fields" className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-medium text-slate-900">
                          {isRTL ? 'الحقول اليدوية' : 'Manual Fields'}
                        </h3>
                        <p className="text-xs text-slate-500">
                          {isRTL 
                            ? 'الحقول التي يملأها الموارد البشرية عند إصدار الخطاب'
                            : 'Fields filled by HR when issuing the letter'}
                        </p>
                      </div>
                      
                      {editMode && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddManualField}
                        >
                          <Plus className="h-4 w-4 mr-1" />
                          {isRTL ? 'إضافة حقل' : 'Add Field'}
                        </Button>
                      )}
                    </div>
                    
                    {editForm.manual_fields.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 border-2 border-dashed rounded-lg">
                        <Info className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>{isRTL ? 'لا توجد حقول يدوية' : 'No manual fields defined'}</p>
                        <p className="text-xs">
                          {isRTL 
                            ? 'جميع البيانات تُملأ تلقائياً من الملف الشخصي'
                            : 'All data will be auto-filled from employee profile'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {editForm.manual_fields.map((field, index) => (
                          <div key={index} className="border rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium">
                                {isRTL ? `حقل ${index + 1}` : `Field ${index + 1}`}
                              </span>
                              {editMode && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => handleRemoveManualField(index)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-xs">Key</Label>
                                <Input
                                  value={field.key}
                                  onChange={(e) => handleUpdateManualField(index, 'key', e.target.value)}
                                  disabled={!editMode}
                                  placeholder="field_key"
                                  className="font-mono text-sm"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Type</Label>
                                <Select
                                  value={field.field_type}
                                  onValueChange={(v) => handleUpdateManualField(index, 'field_type', v)}
                                  disabled={!editMode}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {FIELD_TYPES.map(ft => (
                                      <SelectItem key={ft.value} value={ft.value}>
                                        {ft.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs">Label (AR)</Label>
                                <Input
                                  value={field.label_ar}
                                  onChange={(e) => handleUpdateManualField(index, 'label_ar', e.target.value)}
                                  disabled={!editMode}
                                  dir="rtl"
                                />
                              </div>
                              <div>
                                <Label className="text-xs">Label (EN)</Label>
                                <Input
                                  value={field.label_en}
                                  onChange={(e) => handleUpdateManualField(index, 'label_en', e.target.value)}
                                  disabled={!editMode}
                                />
                              </div>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <Checkbox
                                id={`required-${index}`}
                                checked={field.required}
                                onCheckedChange={(checked) => handleUpdateManualField(index, 'required', checked)}
                                disabled={!editMode}
                              />
                              <Label htmlFor={`required-${index}`} className="text-sm">
                                {isRTL ? 'مطلوب' : 'Required'}
                              </Label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </TabsContent>
                  
                  {/* Info Tab */}
                  <TabsContent value="info" className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">{isRTL ? 'المعرف:' : 'ID:'}</span>
                        <p className="font-mono text-xs break-all">{selectedTemplate.id}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">{isRTL ? 'الإصدار:' : 'Version:'}</span>
                        <p className="font-medium">{selectedTemplate.version}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">{isRTL ? 'الحالة:' : 'Status:'}</span>
                        <p className="font-medium capitalize">{selectedTemplate.status}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">{isRTL ? 'أنشئ بواسطة:' : 'Created by:'}</span>
                        <p>{selectedTemplate.created_by}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">{isRTL ? 'تاريخ الإنشاء:' : 'Created:'}</span>
                        <p>{new Date(selectedTemplate.created_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">{isRTL ? 'آخر تحديث:' : 'Updated:'}</span>
                        <p>{new Date(selectedTemplate.updated_at).toLocaleString()}</p>
                      </div>
                      {selectedTemplate.published_at && (
                        <div>
                          <span className="text-slate-500">{isRTL ? 'تاريخ النشر:' : 'Published:'}</span>
                          <p>{new Date(selectedTemplate.published_at).toLocaleString()}</p>
                        </div>
                      )}
                      {selectedTemplate.change_notes && (
                        <div className="col-span-2">
                          <span className="text-slate-500">{isRTL ? 'ملاحظات:' : 'Notes:'}</span>
                          <p>{selectedTemplate.change_notes}</p>
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          ) : (
            <CardContent className="flex items-center justify-center h-[500px]">
              <div className="text-center text-slate-400">
                <FileText className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">
                  {isRTL ? 'اختر قالباً للعرض' : 'Select a template to view'}
                </p>
                <p className="text-sm">
                  {isRTL 
                    ? 'اختر من القائمة على اليسار'
                    : 'Choose from the list on the left'}
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
      
      {/* Placeholders Dialog */}
      <Dialog open={showPlaceholdersDialog} onOpenChange={setShowPlaceholdersDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'المتغيرات المتاحة' : 'Available Placeholders'}</DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'انسخ واستخدم هذه المتغيرات في محتوى القالب'
                : 'Copy and use these placeholders in template content'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Group by source */}
            {['company_settings', 'employee', 'system'].map(source => {
              const sourceFields = placeholders.filter(p => p.source === source);
              if (sourceFields.length === 0) return null;
              
              return (
                <div key={source}>
                  <h4 className="text-sm font-medium text-slate-700 mb-2 capitalize">
                    {source.replace('_', ' ')}
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    {sourceFields.map(p => (
                      <div
                        key={p.key}
                        className="flex items-center justify-between p-2 bg-slate-50 rounded-lg hover:bg-slate-100 cursor-pointer"
                        onClick={() => copyPlaceholder(p.key)}
                      >
                        <div>
                          <code className="text-sm text-violet-600">{`{{${p.key}}}`}</code>
                          <p className="text-xs text-slate-500">
                            {isRTL ? p.label_ar : p.label_en}
                          </p>
                        </div>
                        <Copy className="h-4 w-4 text-slate-400" />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{isRTL ? 'معاينة القالب' : 'Template Preview'}</DialogTitle>
            <DialogDescription>
              {isRTL ? 'معاينة ببيانات تجريبية' : 'Preview with sample data'}
            </DialogDescription>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Button
                  variant={previewLanguage === 'ar' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviewLanguage('ar')}
                >
                  العربية
                </Button>
                <Button
                  variant={previewLanguage === 'en' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setPreviewLanguage('en')}
                >
                  English
                </Button>
              </div>
              
              <div 
                className={cn(
                  "border rounded-lg p-6 bg-white min-h-[400px]",
                  previewLanguage === 'ar' && "text-right"
                )}
                dir={previewLanguage === 'ar' ? 'rtl' : 'ltr'}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(previewLanguage === 'ar' 
                    ? previewData.preview_ar 
                    : previewData.preview_en) }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Publish Confirmation Dialog */}
      <Dialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تأكيد النشر' : 'Confirm Publish'}</DialogTitle>
            <DialogDescription>
              {isRTL 
                ? `هل تريد نشر الإصدار ${selectedTemplate?.version}؟ سيتم أرشفة الإصدار المنشور الحالي.`
                : `Publish version ${selectedTemplate?.version}? The current published version will be archived.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPublishDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={handlePublishTemplate}
              disabled={saving}
            >
              <CheckCircle2 className="h-4 w-4 mr-2" />
              {saving ? '...' : isRTL ? 'نشر' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Archive Confirmation Dialog */}
      <Dialog open={showArchiveDialog} onOpenChange={setShowArchiveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isRTL ? 'تأكيد الأرشفة' : 'Confirm Archive'}</DialogTitle>
            <DialogDescription>
              {isRTL 
                ? 'هل تريد أرشفة هذا القالب؟ لن يكون متاحاً لإصدار خطابات جديدة.'
                : 'Archive this template? It will no longer be available for issuing new letters.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveDialog(false)}>
              {isRTL ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              variant="destructive"
              onClick={handleArchiveTemplate}
              disabled={saving}
            >
              <Archive className="h-4 w-4 mr-2" />
              {saving ? '...' : isRTL ? 'أرشفة' : 'Archive'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
